import { useEffect, useState } from 'react';
import { View, StyleSheet, Text, ScrollView, ActivityIndicator, Image, TouchableOpacity } from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { RootStackParamList } from '../types/navigation';
import { VideoPost } from '../types/video';
import VideoCard from '../components/video/VideoCard';
import { getProfile, getAuthorVideoPosts, followUser, unfollowUser } from '../services/bluesky';
import { convertPostToVideoPost, formatViews } from '../utils/video';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

type ChannelDetailRouteProp = RouteProp<RootStackParamList, 'ChannelDetail'>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const ChannelDetailScreen = () => {
  const route = useRoute<ChannelDetailRouteProp>();
  const navigation = useNavigation<NavigationProp>();
  const { did, handle } = route.params;
  
  const [profile, setProfile] = useState<any>(null);
  const [videos, setVideos] = useState<VideoPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followUri, setFollowUri] = useState<string | undefined>(undefined);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    fetchChannelData();
  }, [did, handle]);

  const fetchChannelData = async () => {
    try {
      setLoading(true);
      
      // プロフィール情報を取得
      const profileData = await getProfile(did || handle || '');
      setProfile(profileData);
      setIsFollowing(!!profileData.viewer?.following);
      setFollowUri(profileData.viewer?.following);

      // 動画投稿を取得
      const videosResult = await getAuthorVideoPosts(profileData.handle);
      const videoList = videosResult.feed
        .map(convertPostToVideoPost)
        .filter((v): v is VideoPost => v !== null);
      setVideos(videoList);
    } catch (error) {
      console.error('Failed to fetch channel data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!profile || followLoading) return;

    try {
      setFollowLoading(true);
      
      if (isFollowing && followUri) {
        await unfollowUser(followUri);
        setIsFollowing(false);
        setFollowUri(undefined);
      } else {
        const uri = await followUser(profile.did);
        setIsFollowing(true);
        setFollowUri(uri);
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#fff" />
      </View>
    );
  }

  if (!profile) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>チャンネルが見つかりませんでした</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* チャンネルヘッダー */}
      <View style={styles.header}>
        {profile.banner && (
          <Image source={{ uri: profile.banner }} style={styles.banner} />
        )}
        <View style={styles.profileSection}>
          {profile.avatar ? (
            <Image source={{ uri: profile.avatar }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, { backgroundColor: '#333' }]} />
          )}
          <View style={styles.profileInfo}>
            <Text style={styles.channelName}>{profile.displayName || profile.handle}</Text>
            <Text style={styles.handle}>@{profile.handle}</Text>
            <Text style={styles.stats}>
              {profile.followersCount ? `${formatViews(profile.followersCount)} 登録者` : '登録者数不明'}
              {' · '}
              {videos.length} 本の動画
            </Text>
          </View>
        </View>

        {profile.description && (
          <Text style={styles.description}>{profile.description}</Text>
        )}

        <TouchableOpacity 
          style={[styles.subscribeButton, isFollowing && styles.subscribeButtonActive]}
          onPress={handleFollowToggle}
          disabled={followLoading}
        >
          <Text style={styles.subscribeButtonText}>
            {followLoading ? '...' : isFollowing ? 'フォロー中' : 'フォロー'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* タブセクション（動画のみ表示） */}
      <View style={styles.tabsContainer}>
        <View style={[styles.tab, styles.activeTab]}>
          <Ionicons name="play" size={20} color="#fff" />
          <Text style={styles.tabText}>動画</Text>
        </View>
      </View>

      {/* 動画リスト */}
      <View style={styles.videosContainer}>
        {videos.length > 0 ? (
          videos.map((video) => (
            <VideoCard
              key={video.id}
              thumbnailUrl={video.thumbnailUrl}
              title={video.title}
              channelName={video.channelName}
              channelAvatar={video.channelAvatar}
              likeCount={video.likeCount}
              createdAt={video.createdAt}
              duration={video.duration}
              videoUrl={video.videoUrl}
              videoType={video.type}
              onPress={() => navigation.navigate('VideoPlayback', { videoId: video.id, video })}
            />
          ))
        ) : (
          <Text style={styles.noVideosText}>このチャンネルには動画がありません</Text>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
  },
  header: {
    backgroundColor: '#111',
    paddingBottom: 20,
  },
  banner: {
    width: '100%',
    height: 150,
    backgroundColor: '#222',
  },
  profileSection: {
    flexDirection: 'row',
    padding: 16,
    alignItems: 'center',
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginRight: 16,
  },
  profileInfo: {
    flex: 1,
  },
  channelName: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  handle: {
    color: '#aaa',
    fontSize: 14,
    marginBottom: 8,
  },
  stats: {
    color: '#aaa',
    fontSize: 13,
  },
  description: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  subscribeButton: {
    backgroundColor: '#c00',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 24,
    alignSelf: 'flex-start',
    marginHorizontal: 16,
  },
  subscribeButtonActive: {
    backgroundColor: '#555',
  },
  subscribeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  tabsContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 16,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#fff',
  },
  tabText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    marginLeft: 6,
  },
  videosContainer: {
    padding: 8,
  },
  noVideosText: {
    color: '#aaa',
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 40,
  },
});

export default ChannelDetailScreen;
